import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

// material
import { withStyles } from '@material-ui/core/styles';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DownloadIcon from '@material-ui/icons/CloudDownload';

// ui
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPMonthYearPicker from '../../components/pureForm/FMPMonthYearPicker';
import FMPCheckbox from '../../components/pureForm/FMPCheckbox';
import FuelContractFulfillmentContainer from '../../containers/inventory/FuelContractFulfillment';
import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import ContractPricingPopup from '../../components/Dialogs/ContractPricingPopup';
import PricingPopupIcon from '../../images/icons/pricingPopup.svg';
import documentIcon from '../../images/document.svg';

import { getSharedStyles } from '../../themes';
import FormComponent from '../../components/FormComponent';
import { search } from '../../modules/inventory/fuelContractFulfillment';
import { valueToDisplay } from '../../libs/products';

const isLocalhost = Boolean(window.location.hostname === 'localhost');

const currentColumns = [
	{ displayId: 'idDisplay', label: 'Contract #s', flex: 0.8 },
	{ displayId: 'contractDisplay', label: 'Fuel Vendor', flex: 1.5 },
	{ displayId: 'allocationDisplay', label: 'Allocation', flex: 1.5 },
	{ displayId: 'quarterDisplay', labels: ['Qtr', 'Period'], flex: 0.8 },
	{ displayId: 'startEndDateDisplay', labels: ['Start', 'End'], flex: 1 },
	{ displayId: 'costsDisplay', label: 'Costs', flex: 1.2 },
	{ displayId: 'railcarFulfillmentDisplay', label: 'Railcars', flex: 1 },
	{ displayId: 'gallonFulfillmentDisplay', label: 'Gallons', flex: 1.2 },
	{ displayId: 'editButtons', label: 'Edit', flex: 0.6 },
];

const styles = (theme) => ({
	...getSharedStyles(theme),
	bigButton: {
		borderRadius: '100px',
		padding: '18px 34px',
		fontSize: 16,
	},
	dateRange: {
		marginRight: 10,
	},
	pricingIcon: {
		marginRight: 12,
		display: 'inline-block',
		width: 34,
		height: 31,
		backgroundImage: `url('${PricingPopupIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
	documentIcon: {
		marginRight: 12,
		display: 'inline-block',
		width: 34,
		height: 40,
		backgroundImage: `url('${documentIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
	docContainer: {
		position: 'absolute',
	},
	summaryGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
		gap: '4px 12px',
		width: '100%',
		textAlign: 'left',
	},
	summarySection: {
		marginBottom: 4,
	},
	summaryLabel: {
		fontSize: '0.7rem',
		color: '#888',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	summaryValue: {
		fontSize: '0.875rem',
		fontWeight: 600,
		color: '#222',
	},
	summarySubvalue: {
		fontSize: '0.8rem',
		color: '#555',
	},
	fulfillmentSection: {
		marginBottom: 4,
		borderLeft: '2px solid #1976d2',
		paddingLeft: 10,
	},
	fulfilledDate: {
		color: '#4CAF50',
		fontWeight: 600,
	},
	noRailcars: {
		color: '#888',
		fontStyle: 'italic',
		padding: 8,
	},
	detailsSection: {
		width: '100%',
		flexDirection: 'column',
	},
	summaryBar: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0 24px',
		padding: '16px 20px',
		background: '#fff',
		borderRadius: 4,
		boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
		marginBottom: 16,
	},
	summaryBarStat: {
		minWidth: 140,
		marginBottom: 8,
	},
	summaryBarLabel: {
		fontSize: '0.7rem',
		color: '#888',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	summaryBarValue: {
		fontSize: '1.4rem',
		fontWeight: 700,
		color: '#222',
		lineHeight: 1.2,
	},
	summaryBarSubvalue: {
		fontSize: '0.75rem',
		color: '#555',
	},
	summaryBarDivider: {
		width: 1,
		background: '#e0e0e0',
		margin: '0 8px',
		alignSelf: 'stretch',
	},
	columnHeaderRow: {
		display: 'flex',
		alignItems: 'center',
		padding: '8px 64px 8px 16px',
		background: '#f5f5f5',
		borderBottom: '2px solid #ddd',
		borderRadius: '4px 4px 0 0',
		marginBottom: 0,
	},
	columnHeaderCell: {
		fontSize: '0.7rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#555',
		paddingRight: 8,
	},
	columnRow: {
		display: 'flex',
		alignItems: 'flex-start',
		width: '100%',
	},
	columnCell: {
		paddingRight: 8,
		fontSize: '0.875rem',
		textAlign: 'left',
	},
	columnLabel: {
		fontSize: '0.65rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#888',
		marginBottom: 2,
	},
	hasCost: {
		fontWeight: 900,
		color: '#4CAF50',
	},
	missingCost: {
		fontWeight: 900,
		color: '#FF500F',
	},
	dontNeedCostYet: {
		color: '#888',
		fontWeight: 300,
	},
});

class FuelContractFulfillment extends FormComponent {
	constructor(props) {
		super(props);

		const dateRange = 'lastMonth';
		const { yearMonth } = this.dateRangeToDates(dateRange);
		this.state = {
			dateRange,
			yearMonth,
			yearMonthEnd: yearMonth,
			isSearching: false,
			fulfillmentRows: undefined,
			contractMap: {},
			railcarMap: {},
			supplierMap: {},
			terminalMap: {},
			resourceMap: {},
			includeNoRailcars: false,
			isPricingPoppedUp: false,
			fuelContractPoppedUp: undefined,
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			formFieldError: {},
		};
	}

	dateRangeToDates = (dateRange) => {
		if (dateRange === 'thisMonth') {
			return { yearMonth: moment().startOf('month').format('YYYY-MM') };
		} else if (dateRange === 'lastMonth') {
			return { yearMonth: moment().subtract(1, 'month').startOf('month').format('YYYY-MM') };
		} else {
			return {};
		}
	};

	setDateRange = (dateRange) => {
		const { yearMonth } = this.dateRangeToDates(dateRange);
		if (!yearMonth) {
			this.setState({ dateRange });
		} else {
			this.setState({ dateRange, yearMonth, yearMonthEnd: yearMonth });
		}
	};

	handlePricingClick = (e, fuelContract) => {
		e.stopPropagation();
		this.setState({ isPricingPoppedUp: true, fuelContractPoppedUp: fuelContract });
	};

	handleClosePricingPopup = () => {
		this.setState({ isPricingPoppedUp: false, fuelContractPoppedUp: undefined });
	};

	handleResourcesClick = (e, resourceIds) => {
		e.stopPropagation();
		this.setState({ isResourcePopupOpen: true, resourceIdsPoppedUp: resourceIds });
	};

	handleCloseResourcePopup = () => {
		this.setState({ isResourcePopupOpen: false, resourceIdsPoppedUp: [] });
	};

	submitSearch = () => {
		const { showErrors } = this.props;
		const { yearMonth, yearMonthEnd, dateRange } = this.state;

		this.setState({ isSearching: true });

		const afterDate = yearMonth
			? moment(yearMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
			: undefined;

		const beforeDate = yearMonthEnd
			? moment(yearMonthEnd, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
			: undefined;

		search({
			afterDate,
			beforeDate,
			callback: (err, result) => {
				this.setState({ isSearching: false });
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}
				const {
					fuelContractFulfillmentCollection,
					fuelContractCollection,
					railcarCollection,
					supplierCollection,
					terminalCollection,
					resourceCollection,
				} = result.collections;

				this.setState({
					fulfillmentRows: fuelContractFulfillmentCollection,
					contractMap: _.keyBy(fuelContractCollection, 'fuelContractId'),
					railcarMap: _.keyBy(railcarCollection, 'railcarId'),
					supplierMap: _.keyBy(supplierCollection, 'supplierId'),
					terminalMap: _.keyBy(terminalCollection, 'terminalId'),
					resourceMap: _.keyBy(resourceCollection, 'resourceId'),
				});
			},
		});
	};

	getApiParams = () => {
		const { accessToken } = this.props;
		const { yearMonth, yearMonthEnd } = this.state;
		const afterDate = yearMonth
			? moment(yearMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
			: undefined;
		const beforeDate = yearMonthEnd
			? moment(yearMonthEnd, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
			: undefined;
		return { accessToken, afterDate, beforeDate };
	};

	renderDateButton = (label, range) => {
		const { classes } = this.props;
		const { dateRange } = this.state;
		const variant = dateRange === range ? 'contained' : 'outlined';

		return (
			<Button
				className={classes.dateRange}
				variant={variant}
				color="primary"
				onClick={() => this.setDateRange(range)}
			>
				{label}
			</Button>
		);
	};

	renderDateRangeOptions = () => {
		const { dateRange } = this.state;
		const isCustomRange = dateRange === 'custom';

		return (
			<>
				<Grid item md={12}>
					<FMPSectionHeader label={'Date Range'} isTop={true} />
				</Grid>

				<Grid item md={12} style={{ display: 'flex' }}>
					{this.renderDateButton('Current Month', 'thisMonth')}
					{this.renderDateButton('Last Month', 'lastMonth')}
					{this.renderDateButton('Custom', 'custom')}
				</Grid>

				<FMPMonthYearPicker
					{...this.setForm('yearMonth', isCustomRange ? 'Start Month / Year' : 'Month / Year')}
					disabled={!isCustomRange}
					showFuture={true}
					md={6}
				/>

				{isCustomRange && (
					<FMPMonthYearPicker
						{...this.setForm('yearMonthEnd', 'End Month / Year')}
						disabled={false}
						showFuture={true}
						md={6}
					/>
				)}
			</>
		);
	};

	renderSearchOptions = () => {
		const { classes } = this.props;
		const { isSearching } = this.state;

		return (
			<>
				<FMPHeadline label="Contract Fulfillment" />
				<Paper className={classes.paper}>
					<Grid container spacing={2}>
						{this.renderDateRangeOptions()}

						<FMPCheckbox
							{...this.setForm('includeNoRailcars', 'Include contracts without railcars', false, false)}
							value={this.state.includeNoRailcars}
							checked={this.state.includeNoRailcars}
							md={12}
						/>

						<Grid item md={12} style={{ paddingTop: 16, display: 'flex', alignItems: 'center' }}>
							<Button
								className={classes.bigButton}
								variant="contained"
								color="primary"
								onClick={this.submitSearch}
								disabled={isSearching}
							>
								<SearchIcon className={classes.icon} />
								Contract Fulfillment Search
							</Button>

							{(() => {
								const params = this.getApiParams();
								params.downloadCsv = true;
								const request = { method: 'fuelContractFulfillment.search', params };
								let host = '';
								if (isLocalhost) {
									host = 'http://localhost:3700';
								}
								const downloadUrl = `${host}/api?request=${JSON.stringify(request)}`;
								return (
									<a
										href={downloadUrl}
										style={{ textDecoration: 'none', paddingLeft: 10 }}
										target="_blank"
										rel="noreferrer noopener"
									>
										<Button
											className={classes.bigButton}
											variant="contained"
											color="primary"
											disabled={isSearching}
										>
											<DownloadIcon className={classes.icon} />
											&nbsp;Download CSV
										</Button>
									</a>
								);
							})()}

							{isSearching && (
								<div className={classes.runningSearch} style={{ marginLeft: 16 }}>
									<CircularProgress />
									<span style={{ paddingLeft: 5 }}>Searching...</span>
								</div>
							)}
						</Grid>
					</Grid>
				</Paper>
			</>
		);
	};

	renderSummaryBar = (visibleRows) => {
		const { classes } = this.props;

		const totalContracts = visibleRows.length;
		const contractedRailcars = _.sumBy(visibleRows, 'contractedCount');
		const fulfilledRailcars = _.sumBy(visibleRows, 'fulfilledCount');
		const receivedRailcars = _.sumBy(visibleRows, 'receivedCount');
		const contractedGallons = _.sumBy(visibleRows, 'contractedGallons');
		const fulfilledGallons = _.sumBy(visibleRows, 'fulfilledGallons');
		const receivedGallons = _.sumBy(visibleRows, 'receivedGallons');

		const stat = (label, value, subvalue) => (
			<div className={classes.summaryBarStat}>
				<div className={classes.summaryBarLabel}>{label}</div>
				<div className={classes.summaryBarValue}>{value}</div>
				{subvalue && <div className={classes.summaryBarSubvalue}>{subvalue}</div>}
			</div>
		);

		return (
			<div className={classes.summaryBar}>
				{stat('Contracts', totalContracts.toLocaleString())}
				<div className={classes.summaryBarDivider} />
				{stat('Contracted Railcars', contractedRailcars.toLocaleString())}
				{stat('Fulfilled Railcars', fulfilledRailcars.toLocaleString())}
				{stat('Received Railcars', receivedRailcars.toLocaleString())}
				<div className={classes.summaryBarDivider} />
				{stat('Contracted Gallons', contractedGallons.toLocaleString())}
				{stat('Fulfilled Gallons', fulfilledGallons.toLocaleString())}
				{stat('Received Gallons', receivedGallons.toLocaleString())}
			</div>
		);
	};

	renderRailcarTable = (railcarIds) => {
		const { classes } = this.props;
		const { railcarMap } = this.state;

		if (_.isEmpty(railcarIds)) {
			return (
				<div className={classes.noRailcars}>No railcars matched this contract</div>
			);
		}

		const fmtDate = (d) => (d ? moment(d).format('MMM-D') : '-');
		const formatNum = (val) =>
			val == null ? '-' : new Intl.NumberFormat('en-US').format(val);

		const railcars = _.compact(railcarIds.map((id) => railcarMap[id]));

		return (
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Railcar #</TableCell>
						<TableCell>Supplier</TableCell>
						<TableCell>Product</TableCell>
						<TableCell>PO #</TableCell>
						<TableCell>CP Date</TableCell>
						<TableCell>Date Rec&apos;d</TableCell>
						<TableCell>Date Empty</TableCell>
						<TableCell>Date Rel</TableCell>
						<TableCell>Net Gal</TableCell>
						<TableCell>Gross Gal</TableCell>
						</TableRow>
				</TableHead>
				<TableBody>
					{railcars.map((railcar) => {
						const poDisplay = railcar.requestedMonth
							? `${moment(railcar.requestedMonth).format('MMM')} (#${railcar.requestedNumber || '-'})`
							: '-';
						const dateRecd = railcar.apDate ? fmtDate(railcar.apDate) : null;
							const etaDisplay = !railcar.apDate && railcar.etaDate ? fmtDate(railcar.etaDate) : null;

						return (
							<TableRow key={railcar.railcarId}>
								<TableCell>{railcar.railcarNumber || '-'}</TableCell>
								<TableCell>{railcar.fuelSupplier || '-'}</TableCell>
								<TableCell>{railcar.product || '-'}</TableCell>
								<TableCell>{poDisplay}</TableCell>
								<TableCell>
									{railcar.cpDate ? (
										<span className={classes.fulfilledDate}>
											{fmtDate(railcar.cpDate)}
										</span>
									) : (
										'-'
									)}
								</TableCell>
								<TableCell>{dateRecd || (etaDisplay ? <span style={{ color: '#aaa' }}>ETA: {etaDisplay}</span> : '-')}</TableCell>
								<TableCell>{fmtDate(railcar.emptyDate)}</TableCell>
								<TableCell>{fmtDate(railcar.relinquishedDate)}</TableCell>
								<TableCell>{formatNum(railcar.netGallons)}</TableCell>
								<TableCell>{formatNum(railcar.grossGallons)}</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		);
	};

	renderColumnHeader = () => {
		const { classes } = this.props;
		return (
			<div className={classes.columnHeaderRow}>
				{currentColumns.map((col) => (
					<div
						key={col.displayId}
						className={classes.columnHeaderCell}
						style={{ flex: col.flex || 1 }}
					>
						{col.labels ? col.labels.join(' / ') : col.label}
					</div>
				))}
			</div>
		);
	};

	renderFulfillmentRow = (fulfillmentRow) => {
		const { classes } = this.props;
		const { contractMap, supplierMap, terminalMap } = this.state;
		const {
			fuelContractId,
			railcarIds,
			contractedCount,
			fulfilledCount,
			receivedCount,
			contractedGallons,
			fulfilledGallons,
			receivedGallons,
		} = fulfillmentRow;

		const contract = contractMap[fuelContractId] || {};
		const {
			contractNumber,
			sourceCustomer,
			numberOfGallons,
			numberOfBarrels,
			numberOfRailcars,
			quarter,
			timePeriod,
			startDate,
			endDate,
			productType,
			allocatedForTerminalId,
			supplierForTerminalId,
			fuelVendorSupplierId,
			contractLoadingNumber,
			resourceIds,
			finalCalculatedCosts,
			overUnderMarginForNumberOfGallons,
		} = contract;

		const terminal = terminalMap[allocatedForTerminalId] || {};
		const supplier = supplierMap[supplierForTerminalId] || {};
		const fuelVendorSupplier = fuelVendorSupplierId ? supplierMap[fuelVendorSupplierId] : null;
		const fuelDisplay = valueToDisplay(productType);
		const now = moment();

		const displayProps = {
			idDisplay: (
				<div>
					<div><span style={{ color: '#555' }}>FMP:</span> {fuelContractId}</div>
					<div><span style={{ color: '#555' }}>Pearson:</span> {contractNumber || '-'}</div>
				</div>
			),
			contractDisplay: (
				<div>
					<div>Fuel Vendor: {fuelVendorSupplier ? fuelVendorSupplier.name : '-'}</div>
					<div>Source: {sourceCustomer || '-'}</div>
					{contractLoadingNumber && <div>Loading #:{contractLoadingNumber}</div>}
				</div>
			),
			contractNumbersDisplay: (
				<div>
					<div><span style={{ color: '#555' }}>Pearson:</span> {contractNumber || '-'}</div>
				</div>
			),
			allocationDisplay: (
				<div style={{ fontSize: '90%' }}>
					<div>Fuel: {fuelDisplay || productType || '-'}</div>
					<div>Terminal: {terminal.name || '-'}</div>
					<div>Supplier: {supplier.name || '-'}</div>
				</div>
			),
			gallonDisplay: (
				<div>
					<div>{numberOfGallons != null ? numberOfGallons.toLocaleString() : '-'} contracted</div>
					<div style={{ fontSize: '80%', color: '#777' }}>
						{_.isNil(numberOfBarrels) ? '(-)' : `(${numberOfBarrels})`}
					</div>
				</div>
			),
			railcarDisplay: (
				<div>
					<div>{_.isNil(numberOfRailcars) ? '-' : numberOfRailcars}</div>
				</div>
			),
			quarterDisplay: (
				<div>
					<div>{quarter || '-'}</div>
					<div>{_.capitalize(timePeriod)}</div>
				</div>
			),
			startEndDateDisplay: (
				<div>
					<div>{startDate ? moment(startDate).format('M/D/YYYY') : '-'}</div>
					<div>{endDate ? moment(endDate).format('M/D/YYYY') : '-'}</div>
				</div>
			),
			costsDisplay: (finalCalculatedCosts || []).map((finalCalculatedCost, i) => {
				const { startDate: costStart, endDate: costEnd, cost, bulkCost } = finalCalculatedCost;
				const endMoment = moment(costEnd).endOf('day');
				const month = moment(costStart).format('MMM');
				const shouldBeDone = endMoment.isBefore(now);

				if (shouldBeDone) {
					if (cost && parseFloat(cost)) {
						return (
							<div key={i}>
								<span className={classes.hasCost}>{month}</span>: <strong>{cost}</strong>
								{bulkCost && (
									<span>
										<span style={{ color: '#aaa', paddingLeft: 7, paddingRight: 7 }}>|</span>
										<strong>{bulkCost}</strong>
									</span>
								)}
							</div>
						);
					} else {
						return (
							<div key={i}>
								<span className={classes.missingCost}>{month}</span>: <span>????</span>
							</div>
						);
					}
				} else {
					return (
						<div key={i} className={classes.dontNeedCostYet}>
							<span className={classes.dontNeedCostYet}>{month}</span>:{' '}
							<span>{cost || 'n/a'}</span>
						</div>
					);
				}
			}),
			railcarFulfillmentDisplay: (() => {
				const isOver = fulfilledCount > contractedCount;
				return (
					<div>
						<div><span style={{ color: '#888', fontSize: '0.75rem' }}>Contracted:</span> {contractedCount}</div>
						<div style={isOver ? { color: 'red', fontWeight: 700 } : undefined}>
							<span style={{ color: isOver ? 'red' : '#888', fontSize: '0.75rem', fontWeight: 'normal' }}>Fulfilled:</span> {fulfilledCount}
						</div>
						<div><span style={{ color: '#888', fontSize: '0.75rem' }}>Received:</span> {receivedCount}</div>
					</div>
				);
			})(),
			gallonFulfillmentDisplay: (() => {
				const marginPct = overUnderMarginForNumberOfGallons
					? parseFloat(String(overUnderMarginForNumberOfGallons).replace('%', ''))
					: 0;
				const ceiling =
					contractedGallons > 0 && _.isFinite(marginPct)
						? contractedGallons * (1 + marginPct / 100)
						: contractedGallons;
				const isOver = ceiling > 0 && fulfilledGallons > ceiling;
				return (
					<div>
						<div><span style={{ color: '#888', fontSize: '0.75rem' }}>Contracted:</span> {contractedGallons > 0 ? contractedGallons.toLocaleString() : '-'}{marginPct > 0 ? ` (+${marginPct}%)` : ''}</div>
						<div style={isOver ? { color: 'red', fontWeight: 700 } : undefined}>
							<span style={{ color: isOver ? 'red' : '#888', fontSize: '0.75rem', fontWeight: 'normal' }}>Fulfilled:</span> {(fulfilledGallons || 0).toLocaleString()}
							{isOver && <span style={{ fontSize: '0.75rem', marginLeft: 4 }}>⚠ over</span>}
						</div>
						<div><span style={{ color: '#888', fontSize: '0.75rem' }}>Received:</span> {(receivedGallons || 0).toLocaleString()}</div>
					</div>
				);
			})(),
			editButtons: (
				<Button
					size="small"
					variant="outlined"
					onClick={(e) => {
						e.stopPropagation();
						this.props.history.push(`/inventoryManagement/fuelContract/update/${fuelContractId}`);
					}}
				>
					Edit
				</Button>
			),
		};

		return (
			<Accordion key={fuelContractId} defaultExpanded style={{ marginBottom: 8 }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<div className={classes.columnRow}>
						{currentColumns.map((col) => (
							<div
								key={col.displayId}
								className={classes.columnCell}
								style={{ flex: col.flex || 1 }}
							>
								<div className={classes.columnLabel}>
									{col.labels ? col.labels.join(' / ') : col.label}
								</div>
								{displayProps[col.displayId]}
							</div>
						))}
					</div>
				</AccordionSummary>
				<AccordionDetails className={classes.detailsSection}>
					{this.renderRailcarTable(railcarIds)}
				</AccordionDetails>
			</Accordion>
		);
	};

	renderResults = () => {
		const { classes } = this.props;
		const { fulfillmentRows, isSearching, includeNoRailcars } = this.state;

		if (fulfillmentRows === undefined) {
			return (
				<div style={{ color: 'gray', fontStyle: 'italic' }}>
					Click search to see contract fulfillment
				</div>
			);
		}

		if (isSearching) {
			return (
				<div className={classes.runningSearch}>
					<CircularProgress />
					<span style={{ paddingLeft: 5 }}>Searching...</span>
				</div>
			);
		}

		const visibleRows = includeNoRailcars
			? fulfillmentRows
			: fulfillmentRows.filter((r) => r.railcarIds.length > 0);

		if (_.isEmpty(visibleRows)) {
			return <div style={{ color: 'gray', fontStyle: 'italic' }}>No contracts found</div>;
		}

		return (
			<div>
				{this.renderSummaryBar(visibleRows)}
				{visibleRows.map((row) => this.renderFulfillmentRow(row))}
			</div>
		);
	};

	render() {
		const { canRwContracts } = this.props;

		if (!canRwContracts) {
			return <div>Permission denied</div>;
		}

		return (
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					{this.renderSearchOptions()}
				</Grid>
				<Grid item md={12} xs={12}>
					{this.renderResults()}
				</Grid>

				<ContractPricingPopup
					open={this.state.isPricingPoppedUp}
					onClose={this.handleClosePricingPopup}
					fuelContract={this.state.fuelContractPoppedUp}
				/>

				<ResourcePopup
					open={this.state.isResourcePopupOpen}
					onClose={this.handleCloseResourcePopup}
					resourceIds={this.state.resourceIdsPoppedUp}
					resourceMap={this.state.resourceMap}
				/>
			</Grid>
		);
	}
}

FuelContractFulfillment.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	canRwContracts: PropTypes.bool,
	accessToken: PropTypes.string,
	history: PropTypes.object,
};

export default _.flow([withStyles(styles, { withTheme: true }), FuelContractFulfillmentContainer])(
	FuelContractFulfillment
);
